import { useState, useEffect, useCallback, useRef } from 'react';
import { KeyEvent, KeyPress, LiveTestState } from '../types';

interface UseKeyCaptureOptions {
    key1: string;
    key2: string;
}

interface UseKeyCaptureReturn {
    state: LiveTestState;
    startCapture: () => void;
    stopCapture: () => void;
    reset: () => void;
}

/**
 * Hook for capturing keyboard keypresses with precise timing.
 * Tracks keydown and keyup events to calculate hold times.
 */
export function useKeyCapture(options: UseKeyCaptureOptions): UseKeyCaptureReturn {
    const { key1, key2 } = options;

    const [state, setState] = useState<LiveTestState>({
        isRunning: false,
        startTime: null,
        events: [],
        keypresses: [],
        pendingKeys: new Map()
    });

    // Use ref for mutable state that doesn't trigger re-renders
    const pendingKeysRef = useRef<Map<string, number>>(new Map());
    const startTimeRef = useRef<number | null>(null);
    const eventsRef = useRef<KeyEvent[]>([]);
    const keypressesRef = useRef<KeyPress[]>([]);

    // Track which keys we're monitoring
    const trackedKeys = [key1.toLowerCase(), key2.toLowerCase()];

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        const key = event.key.toLowerCase();

        // Only track configured keys
        if (!trackedKeys.includes(key)) return;

        // Ignore key repeat events
        if (event.repeat) return;

        const now = performance.now();
        const timestamp = startTimeRef.current ? now - startTimeRef.current : 0;

        // Record keydown event
        const keyEvent: KeyEvent = {
            key,
            type: 'down',
            timestamp
        };
        eventsRef.current.push(keyEvent);

        // Track pending keydown for hold time calculation
        if (!pendingKeysRef.current.has(key)) {
            pendingKeysRef.current.set(key, timestamp);
        }

        // Update state for real-time display
        setState(prev => ({
            ...prev,
            events: [...eventsRef.current],
            pendingKeys: new Map(pendingKeysRef.current)
        }));
    }, [trackedKeys]);

    const handleKeyUp = useCallback((event: KeyboardEvent) => {
        const key = event.key.toLowerCase();

        // Only track configured keys
        if (!trackedKeys.includes(key)) return;

        const now = performance.now();
        const timestamp = startTimeRef.current ? now - startTimeRef.current : 0;

        // Record keyup event
        const keyEvent: KeyEvent = {
            key,
            type: 'up',
            timestamp
        };
        eventsRef.current.push(keyEvent);

        // Calculate hold time and create keypress record
        const keydownTime = pendingKeysRef.current.get(key);
        if (keydownTime !== undefined) {
            const holdTime = timestamp - keydownTime;
            const keypress: KeyPress = {
                key,
                startTime: keydownTime,
                endTime: timestamp,
                holdTime
            };
            keypressesRef.current.push(keypress);
            pendingKeysRef.current.delete(key);
        }

        // Update state for real-time display
        setState(prev => ({
            ...prev,
            events: [...eventsRef.current],
            keypresses: [...keypressesRef.current],
            pendingKeys: new Map(pendingKeysRef.current)
        }));
    }, [trackedKeys]);

    const startCapture = useCallback(() => {
        // Reset state
        eventsRef.current = [];
        keypressesRef.current = [];
        pendingKeysRef.current.clear();
        startTimeRef.current = performance.now();

        setState({
            isRunning: true,
            startTime: startTimeRef.current,
            events: [],
            keypresses: [],
            pendingKeys: new Map()
        });
    }, []);

    const stopCapture = useCallback(() => {
        // Finalize any pending keypresses (keys held when stop is pressed)
        const now = performance.now();
        const timestamp = startTimeRef.current ? now - startTimeRef.current : 0;

        for (const [key, keydownTime] of pendingKeysRef.current.entries()) {
            const holdTime = timestamp - keydownTime;
            const keypress: KeyPress = {
                key,
                startTime: keydownTime,
                endTime: timestamp,
                holdTime
            };
            keypressesRef.current.push(keypress);
        }
        pendingKeysRef.current.clear();

        setState(prev => ({
            ...prev,
            isRunning: false,
            keypresses: [...keypressesRef.current],
            pendingKeys: new Map()
        }));
    }, []);

    const reset = useCallback(() => {
        eventsRef.current = [];
        keypressesRef.current = [];
        pendingKeysRef.current.clear();
        startTimeRef.current = null;

        setState({
            isRunning: false,
            startTime: null,
            events: [],
            keypresses: [],
            pendingKeys: new Map()
        });
    }, []);

    // Set up event listeners when capturing is active
    useEffect(() => {
        if (state.isRunning) {
            window.addEventListener('keydown', handleKeyDown);
            window.addEventListener('keyup', handleKeyUp);

            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                window.removeEventListener('keyup', handleKeyUp);
            };
        }
    }, [state.isRunning, handleKeyDown, handleKeyUp]);

    return {
        state,
        startCapture,
        stopCapture,
        reset
    };
}

export default useKeyCapture;